import React, { useEffect, useState } from "react";
import { Users, Building2, TrendingUp, MessageSquare, Trash2, Plus, Inbox, Mail, Phone, Home } from "lucide-react";
import api, { inr } from "../../api";
import PropertyForm from "../../components/PropertyForm";

const ROLES = ["admin", "agent", "owner", "buyer", "visitor"];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-6" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <Icon className="w-7 h-7 text-brand-500 mb-3" />
      <div className="text-3xl font-bold text-neutral-900">{value}</div>
      <div className="text-neutral-500 text-sm mt-1">{label}</div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-neutral-500 py-10 text-center">Loading…</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} />
        <StatCard icon={Building2} label="Properties" value={stats.total_properties} />
        <StatCard icon={MessageSquare} label="Leads" value={stats.total_leads} />
        <StatCard icon={TrendingUp} label="Requirements" value={stats.total_requirements} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Listings split</h3>
          <div className="flex gap-6">
            <div><div className="text-2xl font-bold text-brand-600">{stats.buy_count}</div><div className="text-neutral-500 text-sm">For Buy</div></div>
            <div><div className="text-2xl font-bold text-brand-600">{stats.rent_count}</div><div className="text-neutral-500 text-sm">For Rent</div></div>
          </div>
        </div>
        <div className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Users by role</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(stats.roles).map(([r, c]) => (
              <div key={r}><div className="text-xl font-bold text-neutral-900">{c}</div><div className="text-neutral-500 text-sm capitalize">{r}</div></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data.users); };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => { await api.put(`/admin/users/${id}`, { role }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; await api.delete(`/admin/users/${id}`); load(); };

  return (
    <div className="bg-surface border border-neutral-200 shadow-card rounded-2xl overflow-hidden" data-testid="admin-users">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-neutral-400 border-b border-neutral-200 bg-neutral-50">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Role</th>
              <th className="text-right p-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-neutral-100" data-testid={`user-row-${u.id}`}>
                <td className="p-4 font-medium text-neutral-900">{u.name}</td>
                <td className="p-4 text-neutral-600">{u.email}</td>
                <td className="p-4">
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`user-role-${u.id}`}
                    className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5 capitalize focus:outline-none focus:border-brand-500">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-red-500 border border-red-300 rounded-lg hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ManageProperties() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { const { data } = await api.get("/properties?limit=200"); setItems(data.properties); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this property?")) return; await api.delete(`/properties/${id}`); load(); };

  if (showForm)
    return (
      <div className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-6 md:p-8">
        <h3 className="text-xl font-bold text-neutral-900 mb-6">Add Property</h3>
        <PropertyForm onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
      </div>
    );

  return (
    <div data-testid="admin-properties">
      <div className="flex justify-between items-center mb-4">
        <p className="text-neutral-500">{items.length} properties</p>
        <button onClick={() => setShowForm(true)} data-testid="admin-add-property-btn"
          className="flex items-center gap-2 px-5 py-2.5 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl active:scale-95">
          <Plus className="w-5 h-5" /> Add Property
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-4 bg-surface border border-neutral-200 shadow-card rounded-xl p-3" data-testid={`admin-property-${p.id}`}>
            <img src={p.image} alt="" className="w-16 h-16 object-cover rounded-lg" />
            <div className="flex-1">
              <p className="font-medium text-neutral-900">{p.title}</p>
              <p className="text-neutral-500 text-sm">{p.area}, {p.city} · {p.listing_type} · {inr(p.price)}</p>
            </div>
            <button onClick={() => del(p.id)} data-testid={`admin-delete-property-${p.id}`}
              className="p-2 text-red-500 border border-red-300 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data.leads)).catch(() => {});
    api.get("/contact").then((r) => setContacts(r.data.contacts)).catch(() => {});
  }, []);
  const items = tab === "leads" ? leads : contacts;
  return (
    <div data-testid="admin-enquiries">
      <div className="flex gap-2 mb-5">
        {[["leads", `Property Enquiries (${leads.length})`], ["contacts", `Contact Messages (${contacts.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`enquiry-tab-${id}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-navy text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:text-neutral-900"}`}>
            {label}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 rounded-2xl text-neutral-500" data-testid="no-enquiries">
          No {tab === "leads" ? "property enquiries" : "contact messages"} yet.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((it) => (
            <div key={it.id} className="bg-surface border border-neutral-200 shadow-card rounded-2xl p-5" data-testid={`enquiry-${it.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-neutral-900">{it.name}</p>
                {it.status && <span className="text-xs px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-600 capitalize">{it.status}</span>}
              </div>
              {it.property_title && <p className="text-neutral-500 text-sm flex items-center gap-1 mt-0.5"><Home className="w-3.5 h-3.5" /> {it.property_title}</p>}
              {it.subject && <p className="text-neutral-500 text-sm mt-0.5">Re: {it.subject}</p>}
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-neutral-600">
                <span className="flex items-center gap-1"><Mail className="w-4 h-4 text-brand-500" /> {it.email}</span>
                {(it.phone || it.mobile) && <span className="flex items-center gap-1"><Phone className="w-4 h-4 text-brand-500" /> {it.phone || it.mobile}</span>}
              </div>
              {it.message && <p className="text-neutral-500 text-sm mt-2 italic">"{it.message}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap gap-2 mb-8">
        {[["overview", "Overview"], ["users", "Manage Users"], ["properties", "Manage Properties"], ["enquiries", "Enquiries"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
            className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${
              tab === id ? "bg-brand-500 text-white" : "bg-surface border border-neutral-200 text-neutral-600 hover:text-neutral-900"
            }`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "users" && <ManageUsers />}
      {tab === "properties" && <ManageProperties />}
      {tab === "enquiries" && <Enquiries />}
    </div>
  );
}
